import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { clearAllCache } from '../providers/QueryProvider';
import logger from '../utils/logger';
import { sanitizeAuthError } from '../utils/authErrors';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Map Supabase user to a normalized shape so every component
// that reads currentUser.uid, .email, .displayName keeps working.
function mapUser(supabaseUser) {
  if (!supabaseUser) return null;
  return {
    uid: supabaseUser.id,
    email: supabaseUser.email,
    displayName:
      supabaseUser.user_metadata?.full_name ||
      supabaseUser.user_metadata?.name ||
      supabaseUser.email,
    photoURL:
      supabaseUser.user_metadata?.avatar_url ||
      supabaseUser.user_metadata?.picture ||
      null,
    // Keep the raw Supabase user available for advanced use
    _supabase: supabaseUser
  };
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  // Guard flag to prevent onAuthStateChange from re-setting user during/after logout
  const loggingOut = useRef(false);

  // Sign up function with optional CAPTCHA token
  const signup = async (email, password, captchaToken) => {
    try {
      setError('');
      const signUpOptions = {
        emailRedirectTo: `${window.location.origin}`
      };
      if (captchaToken) {
        signUpOptions.captchaToken = captchaToken;
      }
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: signUpOptions
      });
      if (authError) throw authError;
      return data;
    } catch (error) {
      setError(sanitizeAuthError(error.message));
      throw error;
    }
  };

  // Login function with optional CAPTCHA token
  const login = async (email, password, captchaToken) => {
    try {
      setError('');
      loggingOut.current = false; // Reset guard for new login
      const loginOptions = {};
      if (captchaToken) {
        loginOptions.captchaToken = captchaToken;
      }
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: loginOptions
      });
      if (authError) throw authError;
      return data;
    } catch (error) {
      setError(sanitizeAuthError(error.message));
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError('');
      const { data, error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (authError) throw authError;
      return data;
    } catch (error) {
      setError(sanitizeAuthError(error.message));
      throw error;
    }
  };

  // Logout function - clears session completely and navigates to root
  const logout = async () => {
    // Set guard FIRST to prevent onAuthStateChange from re-setting user
    loggingOut.current = true;
    try {
      setError('');
      logger.clearUser();
      // Try scope:'global' first - this revokes the refresh token server-side
      // preventing any background token refresh from re-authenticating
      const { error: globalError } = await supabase.auth.signOut({ scope: 'global' });
      if (globalError) {
        // If global fails (403/session missing), fall back to local cleanup
        logger.warn('[Auth] Global signOut failed, falling back to local:', globalError.message);
        const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
        if (localError) {
          logger.warn('[Auth] Local signOut also failed:', localError.message);
        }
      }
    } catch (error) {
      // Network errors or unexpected failures - still proceed with cleanup
      logger.error('[Auth] Logout error (non-blocking):', error.message);
    } finally {
      // Manually clear ALL Supabase auth tokens from localStorage
      // This prevents any stale token from being picked up on reload
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-') || key.includes('supabase')) {
          localStorage.removeItem(key);
        }
      });
      // Clear session storage entries
      sessionStorage.removeItem('crm_initial_redirect_done');
      // Clear React Query cache (memory-only, must be cleared on logout)
      clearAllCache();
      // Clear React state
      setCurrentUser(null);
      setIsPasswordRecovery(false);
      window.history.replaceState(null, '', '/');
    }
  };

  // Password reset - sends email with OTP/link
  const resetPassword = async (email, captchaToken) => {
    try {
      setError('');
      const options = {
        redirectTo: `${window.location.origin}/reset-password`
      };
      if (captchaToken) {
        options.captchaToken = captchaToken;
      }
      const { data, error: authError } = await supabase.auth.resetPasswordForEmail(email, options);
      if (authError) throw authError;
      return data;
    } catch (error) {
      setError(sanitizeAuthError(error.message));
      throw error;
    }
  };

  // Update password (used on reset-password page)
  const updatePassword = async (newPassword) => {
    try {
      setError('');
      const { data, error: authError } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (authError) throw authError;
      return data;
    } catch (error) {
      setError(sanitizeAuthError(error.message));
      throw error;
    }
  };

  // Resend confirmation email
  const resendConfirmation = async (email) => {
    try {
      setError('');
      const { data, error: authError } = await supabase.auth.resend({
        type: 'signup',
        email
      });
      if (authError) throw authError;
      return data;
    } catch (error) {
      setError(sanitizeAuthError(error.message));
      throw error;
    }
  };

  // Clear error function
  const clearError = () => {
    setError('');
  };

  // Update display name function
  const updateDisplayName = async (newDisplayName) => {
    try {
      setError('');
      const { data, error: authError } = await supabase.auth.updateUser({
        data: { full_name: newDisplayName }
      });
      if (authError) throw authError;
      // Update local state to reflect the change
      if (data?.user) {
        setCurrentUser(mapUser(data.user));
      }
      return true;
    } catch (error) {
      setError(sanitizeAuthError(error.message));
      throw error;
    }
  };

  // Migrate chat UIDs from legacy format to Supabase on first login
  const migrateChatUids = async (user) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('migrate_chat_uid_for_user', {
        p_supabase_uid: user.id,
        p_user_email: user.email
      });
      if (rpcError) {
        logger.warn('[Auth] Chat UID migration RPC error (non-blocking):', rpcError.message);
      } else if (data) {
        logger.log('[Auth] Chat UID migration result:', data);
      }
    } catch (err) {
      logger.warn('[Auth] Chat UID migration failed (non-blocking):', err.message);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Get existing session first (handles page refresh)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      // If logout is in progress, ignore any session data
      if (loggingOut.current) {
        setLoading(false);
        return;
      }
      const user = session?.user ?? null;
      setCurrentUser(mapUser(user));
      if (user?.email) {
        logger.setCurrentUser(user.email);
        migrateChatUids(user);
      } else {
        logger.clearUser();
      }
      setLoading(false);
    });

    // Subscribe to auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        // CRITICAL: If logout is in progress, ignore ALL auth events
        // This prevents TOKEN_REFRESHED or SIGNED_IN from re-setting user
        if (loggingOut.current) {
          return;
        }
        const user = session?.user ?? null;
        setCurrentUser(mapUser(user));
        if (user?.email) {
          logger.setCurrentUser(user.email);
          // Migrate chat UIDs on every sign-in event (idempotent)
          if (_event === 'SIGNED_IN') {
            migrateChatUids(user);
          }
        } else {
          logger.clearUser();
        }
        if (_event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    signInWithGoogle,
    logout,
    updateDisplayName,
    resetPassword,
    updatePassword,
    resendConfirmation,
    isPasswordRecovery,
    setIsPasswordRecovery,
    error,
    clearError,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}